import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';


const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, userProfile } = useAuth();
  const { hasProAccess } = useSubscription();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const userMenuRef = useRef(null);

  // Detect theme changes
  useEffect(() => {
    const checkTheme = () => {
      const htmlElement = document.documentElement;
      const isDark = htmlElement?.classList?.contains('dark');
      const themeAttribute = htmlElement?.getAttribute('data-theme');

      // Check if system theme is active
      if (themeAttribute === 'system') {
        setIsDarkTheme('system');
      } else {
        setIsDarkTheme(isDark);
      }
    };

    // Initial check
    checkTheme();

    // Watch for theme changes
    const observer = new MutationObserver(checkTheme);
    observer?.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme']
    });

    return () => observer?.disconnect();
  }, []);

  // Check if user is admin
  const isAdmin = (user?.email === 'demo@rovynbudget.com' || user?.email === 'rovyngroupinc@gmail.com') && userProfile?.role === 'admin';

  // Primary navigation - minimal and focused
  const primaryNavigation = [
  { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
  { label: 'Transactions', path: '/transactions-management', icon: 'CreditCard' },
  { label: 'Budget', path: '/budget-management', icon: 'Target' },
  { label: 'Bills & Recurring', path: '/bills-recurring', icon: 'Calendar', subPages: ['/bills-recurring'] },
  { label: 'Goals', path: '/financial-goals', icon: 'TrendingUp' },
  { label: 'Accounts', path: '/accounts-overview', icon: 'Wallet' },
  { label: 'Rewards', path: '/rewards-center', icon: 'Gift', subPages: ['/rewards-center', '/referral-system'] },
  { label: 'Insights', path: '/financial-insights', icon: 'BarChart3', proBadge: true, subPages: ['/financial-insights', '/financial-reports'] }];


  // Secondary navigation - lightweight features
  const secondaryNavigation = [
  { label: 'Blog', path: '/financial-blog', icon: 'BookOpen' }];


  // Additional features (less prominent) - tools that don't fit main categories
  const additionalFeatures = [
  { label: 'Receipt Upload', path: '/receipt-upload-ocr-processing', icon: 'ScanLine' },
  { label: 'Debt Payoff', path: '/debt-payoff-tracker', icon: 'TrendingDown', proBadge: true }];


  // Admin navigation items (only visible to authorized admins)
  const adminNavigationItems = [
  { label: 'Admin Dashboard', path: '/admin-dashboard', icon: 'Shield' },
  { label: 'User Management', path: '/user-management-analytics', icon: 'Users' },
  { label: 'Rewards Admin', path: '/rewards-system-administration', icon: 'Gift' }];


  const isActive = (path) => {
    if (location?.pathname === path) return true;
    // Check if current path is in subPages
    const navItem = [...primaryNavigation, ...secondaryNavigation, ...additionalFeatures]?.find(
      (item) => item?.subPages?.includes(location?.pathname)
    );
    return navItem?.path === path;
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const renderNavigationItem = (item, isPrimary = false) => {
    const active = isActive(item?.path);
    return (
      <button
        key={item?.path}
        onClick={() => handleNavigation(item?.path)}
        className={`
          flex items-center gap-10 px-14 py-12 rounded-lg font-caption font-medium
          transition-all duration-200 min-h-[48px] w-full text-left group
          ${
        active ?
        'bg-primary/10 text-primary shadow-sm' :
        isPrimary ?
        'text-foreground hover:bg-muted/60 active:scale-[0.98]' :
        'text-muted-foreground hover:bg-muted/40 hover:text-foreground active:scale-[0.98]'}
        `
        }>

        <Icon
          name={item?.icon}
          size={isPrimary ? 22 : 20}
          color={active ? 'var(--color-primary)' : 'currentColor'}
          strokeWidth={active ? 2.5 : 2} />

        <span className={`text-sm ${isPrimary ? 'font-semibold' : 'font-medium'}`}>
          {item?.label}
        </span>
        {item?.proBadge && hasProAccess &&
        <span className="ml-auto inline-flex items-center gap-4 px-6 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
            <Icon name="Crown" size={10} />
            Pro
          </span>
        }
      </button>);

  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[100] bg-card shadow-sm border-b border-border/50 transition-smooth">
        <div className="mx-auto px-24">
          <div className="flex items-center justify-between h-20">
            <div
              className="flex items-center cursor-pointer transition-smooth hover:opacity-80 flex-shrink-0"
              onClick={() => handleNavigation('/dashboard')}>

              <div className="flex items-center justify-center w-10 h-10 rounded-lg transition-smooth hover:opacity-90">
                <img
                  src={
                  isDarkTheme === 'system' ?
                  "/assets/images/rovyn_system-1768211863958.png" :
                  isDarkTheme ?
                  "/assets/images/rovyn_dark_theme-1768211741183.png" : "/assets/images/rovyn_light_theme-1768211391808.png"
                  }
                  alt="Rovyn Logo"
                  className="w-full h-full object-contain" />

              </div>
              <span className={`ml-8 text-xl font-heading font-semibold ${
              isDarkTheme === 'system' || isDarkTheme ? 'text-white' : 'text-secondary'}`
              }>
                ROVYN
              </span>
            </div>

            {/* User Menu - Desktop */}
            <div className="hidden md:flex relative ml-8 flex-shrink-0" ref={userMenuRef}>
              












              {showUserMenu &&
              <div className="absolute right-0 top-full mt-8 w-56 bg-card rounded-lg shadow-elevation-4 border border-border overflow-hidden">
                  <button
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/settings');
                  }}
                  className="w-full flex items-center gap-8 px-16 py-12 hover:bg-muted transition-smooth text-left">

                    <Icon name="Settings" size={20} color="var(--color-foreground)" />
                    <span className="text-base font-caption text-foreground">Settings</span>
                  </button>
                  <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-8 px-16 py-12 hover:bg-muted transition-smooth text-left">

                    <Icon name="LogOut" size={20} color="var(--color-error)" />
                    <span className="text-base font-caption text-error">Sign Out</span>
                  </button>
                </div>
              }
            </div>

            {/* Menu Toggle Button - Visible on all screen sizes */}
            <button
              onClick={toggleMobileMenu}
              className="flex items-center justify-center w-14 h-14 rounded-lg hover:bg-muted transition-smooth active:scale-press"
              aria-label="Toggle menu">

              <Icon
                name={isMobileMenuOpen ? 'X' : 'Menu'}
                size={28}
                color="var(--color-foreground)" />

            </button>
          </div>
        </div>
      </header>
      {isMobileMenuOpen &&
      <div
        className="fixed inset-0 z-[105] bg-background/80 backdrop-blur-sm"
        onClick={() => setIsMobileMenuOpen(false)}>

          <nav
          className="fixed top-20 left-0 right-0 bottom-0 bg-card shadow-elevation-4 overflow-y-auto"
          onClick={(e) => e?.stopPropagation()}>

            <div className="flex flex-col p-20 space-y-6">
              {/* Primary Navigation Section */}
              <div className="space-y-2">
                <div className="px-14 py-8">
                  <h3 className="text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wider">
                    Main
                  </h3>
                </div>
                {primaryNavigation?.map((item) => renderNavigationItem(item, true))}
              </div>

              {/* Divider */}
              <div className="border-t border-border my-4"></div>

              {/* Secondary Navigation Section */}
              <div className="space-y-2">
                <div className="px-14 py-8">
                  <h3 className="text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wider">
                    Learn
                  </h3>
                </div>
                {secondaryNavigation?.map((item) => renderNavigationItem(item, false))}
              </div>

              {/* Additional Features Section */}
              <div className="space-y-2">
                <div className="px-14 py-8">
                  <h3 className="text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wider">
                    Tools
                  </h3>
                </div>
                {additionalFeatures?.map((item) => renderNavigationItem(item, false))}
              </div>

              {/* Admin Section (if applicable) */}
              {isAdmin &&
            <>
                  <div className="border-t border-border my-4"></div>
                  <div className="space-y-2">
                    <div className="px-14 py-8">
                      <h3 className="text-xs font-caption font-semibold text-error uppercase tracking-wider">
                        Administration
                      </h3>
                    </div>
                    {adminNavigationItems?.map((item) => renderNavigationItem(item, false))}
                  </div>
                </>
            }

              {/* Divider */}
              <div className="border-t border-border my-4"></div>

              {/* Utility Section */}
              <div className="space-y-2">
                <div className="px-14 py-8">
                  <h3 className="text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wider">
                    Account
                  </h3>
                </div>
                <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate('/settings');
                }}
                className="flex items-center gap-10 px-14 py-12 rounded-lg font-caption font-medium text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-all duration-200 min-h-[48px] w-full text-left active:scale-[0.98]">

                  <Icon name="Settings" size={20} color="currentColor" />
                  <span className="text-sm font-medium">Settings</span>
                </button>
                <button
                onClick={handleSignOut}
                className="flex items-center gap-10 px-14 py-12 rounded-lg font-caption font-medium text-error hover:bg-error/10 transition-all duration-200 min-h-[48px] w-full text-left active:scale-[0.98]">

                  <Icon name="LogOut" size={20} color="var(--color-error)" />
                  <span className="text-sm font-medium">Sign Out</span>
                </button>
              </div>

              {/* User Info at Bottom */}
              <div className="border-t border-border pt-16 mt-8">
                <div className="px-14 py-12 bg-muted/30 rounded-lg">
                  <p className="text-xs text-muted-foreground font-caption mb-4">Signed in as</p>
                  <p className="text-sm font-caption text-foreground font-medium truncate">
                    {user?.email || 'User'}
                  </p>
                </div>
              </div>
            </div>
          </nav>
        </div>
      }
    </>);


};

export default Header;